// src/components/forms/shared/CustomerInfoSection.tsx
// Componente reutilizable para información del cliente
// Usado por: OrderForm y CotizacionForm

import React from 'react'
import type { TipoDocumento } from '../../../types/orderFormTypes'
import { Input, Select, Button } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { UserIcon, EnvelopeIcon, PhoneIcon, IdentificationIcon, UserCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface CustomerInfoSectionProps {
  // Datos del cliente
  firstName: string
  lastName: string
  email: string
  phone: string
  tipoDocumento: string
  documento: string
  
  // Handlers
  onFirstNameChange: (value: string) => void
  onLastNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onPhoneChange: (value: string) => void
  onTipoDocumentoChange: (value: string) => void
  onDocumentoChange: (value: string) => void
  
  // Configuración
  mode: 'view' | 'edit' | 'create'
  tiposDeDocumento: TipoDocumento[]
  errors: { [key: string]: string }
  
  // Diferencias entre formularios
  clienteEditable?: boolean // Solo para CotizacionForm
  onValidateCliente?: () => void // Solo para CotizacionForm
  validandoCliente?: boolean // Solo para CotizacionForm
  showValidateButton?: boolean // Solo para CotizacionForm
  
  // Validación de email específica de OrderForm
  onEmailValidation?: (email: string) => void
  
  // Fuente de datos (para diferencias entre OrderForm y CotizacionForm)
  firstNameSource?: 'customer' | 'billingAddress' // OrderForm usa 'customer', CotizacionForm usa 'billingAddress'
  documentoField?: 'document' | 'documento' // OrderForm usa 'document', CotizacionForm usa 'documento'
}

export const CustomerInfoSection: React.FC<CustomerInfoSectionProps> = ({
  firstName,
  lastName,
  email,
  phone,
  tipoDocumento,
  documento,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneChange,
  onTipoDocumentoChange,
  onDocumentoChange,
  mode,
  tiposDeDocumento,
  errors,
  clienteEditable = true,
  onValidateCliente,
  validandoCliente = false,
  showValidateButton = false,
  onEmailValidation,
  firstNameSource = 'customer',
  documentoField = 'document'
}) => {
  const isReadOnly = mode === 'view' || !clienteEditable

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value
    if (onEmailValidation) {
      onEmailValidation(emailValue)
    }
    onEmailChange(emailValue)
  }

  // Determinar si tipo documento y documento están readonly
  // En modo 'create': siempre editables inicialmente (antes de validar cliente)
  //   - Solo se bloquean si clienteEditable es false Y hay datos del cliente cargados (firstName, email)
  //     Esto indica que el cliente fue validado exitosamente y los datos fueron cargados
  //   - Si clienteEditable es true, significa que el cliente no se encontró → editable
  // En modo 'edit': bloqueados si firstNameSource === 'billingAddress' (CotizacionForm)
  // En modo 'view': siempre bloqueados
  const clienteValidado = !clienteEditable && firstName !== '' && email !== ''
  
  const isTipoDocumentoReadOnly = mode === 'view' 
    ? true 
    : mode === 'create' 
      ? clienteValidado // Bloqueado solo si el cliente fue validado exitosamente (hay datos cargados)
      : mode === 'edit' && firstNameSource === 'billingAddress'
        ? true
        : mode === 'edit'
          ? false // En modo edit de OrderForm, siempre editables
          : !clienteEditable
  
  const isDocumentoReadOnly = mode === 'view'
    ? true
    : mode === 'create'
      ? clienteValidado // Bloqueado solo si el cliente fue validado exitosamente (hay datos cargados)
      : mode === 'edit' && firstNameSource === 'billingAddress'
        ? true
        : mode === 'edit'
          ? false // En modo edit de OrderForm, siempre editables
          : !clienteEditable

  // Obtener error de documento (puede ser 'document' o 'documento')
  const documentoError = errors.document || errors.documento

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <SectionTitle icon={UserCircleIcon}>Información del Cliente</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Nombre */}
        <FormField
          label="Nombre"
          required
          error={errors.first_name}
          icon={UserIcon}
        >
          <Input
            type="text"
            value={firstName}
            onChange={(e) => {
              const value = e.target.value.slice(0, 30)
              onFirstNameChange(value)
            }}
            readOnly={isReadOnly}
            maxLength={30}
            error={!!errors.first_name}
            errorMessage={errors.first_name}
            mode={mode}
          />
        </FormField>
        
        {/* Apellido */}
        <FormField
          label="Apellidos"
          required
          error={errors.last_name}
          icon={UserIcon}
        >
          <Input
            type="text"
            value={lastName}
            onChange={(e) => {
              const value = e.target.value.slice(0, 30)
              onLastNameChange(value)
            }}
            readOnly={isReadOnly}
            maxLength={30}
            error={!!errors.last_name}
            errorMessage={errors.last_name}
            mode={mode}
          />
        </FormField>
        
        {/* Email */}
        <FormField
          label="Email"
          required
          error={errors.email}
          icon={EnvelopeIcon}
        >
          <Input
            type="email"
            value={email}
            onChange={handleEmailChange}
            readOnly={isReadOnly}
            error={!!errors.email}
            errorMessage={errors.email}
            mode={mode}
          />
        </FormField>
        
        {/* Teléfono */}
        <FormField
          label="Teléfono"
          required
          error={errors.phone}
          icon={PhoneIcon}
        >
          <Input
            type="tel"
            value={phone}
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '').slice(0, 30)
              onPhoneChange(value)
            }}
            readOnly={isReadOnly}
            maxLength={30}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault()
              }
            }}
            error={!!errors.phone}
            errorMessage={errors.phone}
            mode={mode}
          />
        </FormField>
        
        {/* Tipo de Documento */}
        <FormField
          label="Tipo de Documento"
          required
          error={errors.tipo_documento}
          icon={IdentificationIcon}
        >
          <Select
            value={tipoDocumento}
            onChange={(e) => onTipoDocumentoChange(e.target.value)}
            disabled={isTipoDocumentoReadOnly}
            options={[
              { value: '', label: 'Seleccione' },
              ...tiposDeDocumento.map((tipo) => ({
                value: tipo.value,
                label: tipo.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.tipo_documento}
            errorMessage={errors.tipo_documento}
            mode={mode}
            readOnly={isTipoDocumentoReadOnly}
          />
        </FormField>
        
        {/* Documento */}
        <FormField
          label="Documento"
          required
          error={documentoError}
          icon={IdentificationIcon}
        >
          <Input
            type="tel"
            value={documento}
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '').slice(0, 15)
              onDocumentoChange(value)
            }}
            readOnly={isDocumentoReadOnly}
            maxLength={15}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault()
              }
            }}
            error={!!documentoError}
            errorMessage={documentoError}
            mode={mode}
          />
        </FormField>
        
        {/* Botón Validar Cliente solo para CotizacionForm */}
        {showValidateButton && mode !== 'view' && onValidateCliente && (
          <div className="mt-4 flex justify-end col-span-3">
            <Button
              type="button"
              variant="primary"
              onClick={onValidateCliente}
              disabled={validandoCliente}
              isLoading={validandoCliente}
              leftIcon={MagnifyingGlassIcon}
            >
              {validandoCliente ? 'Validando...' : 'Validar Cliente'}
            </Button>
            {errors.validacion_cliente && (
              <div className="text-red-500 text-sm mt-2 text-right w-full">{errors.validacion_cliente}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
